import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Loader2, CreditCard, CheckCircle2, AlertCircle, ExternalLink } from 'lucide-react';

const money = (n) => `£${Number(n || 0).toFixed(2)}`;

/**
 * Shows whether this provider has a working Stripe Connect payout account and lets them
 * start (or finish) onboarding. Used on the Course Provider, Event Host and University
 * Manager dashboards — anywhere the user can sell something and needs to get paid out.
 *
 * The university / provider themselves complete onboarding (their own legal, identity and bank
 * details are entered on Stripe's hosted form); an admin cannot do it on their behalf.
 */
export default function PayoutConnectCard() {
  const { api } = useAuth();
  const [status, setStatus] = useState(null);
  const [earnings, setEarnings] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await api.get('/payments/connect/status');
      setStatus(res.data);
      setError('');
      if (res.data?.charges_enabled && res.data?.payouts_enabled) {
        api.get('/payments/connect/earnings').then(r => setEarnings(r.data)).catch(() => {});
      }
    } catch (err) {
      setStatus(null);
      setError(err?.response?.data?.detail || '');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const startOnboarding = async () => {
    setConnecting(true);
    try {
      const res = await api.post('/payments/connect/onboard', { origin_url: window.location.origin });
      if (res.data.url) window.location.href = res.data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to start payout setup');
      setConnecting(false);
    }
  };

  const openStripeDashboard = async () => {
    try {
      const res = await api.post('/payments/connect/dashboard-link');
      if (res.data.url) window.open(res.data.url, '_blank', 'noopener');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not open the Stripe dashboard');
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  const ready = status?.charges_enabled && status?.payouts_enabled;
  const commission = status?.commission_percent;

  return (
    <Card data-testid="payout-connect-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CreditCard className="w-5 h-5" />
          Payouts
        </CardTitle>
        <CardDescription>
          Connect your own Stripe account to get paid for paid enrollments, tickets and courses.
          {commission != null && <> WorksRun deducts a <strong>{commission}%</strong> platform fee from each sale automatically and transfers the rest to you.</>}
          {' '}<Link to="/pricing" className="text-[#003366] underline font-medium">View subscription plans</Link>
        </CardDescription>
      </CardHeader>
      <CardContent>
        {error && (
          <div className="mb-3 flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="payout-error">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" /><span>{error}</span>
          </div>
        )}
        {ready ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2 p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700">
              <CheckCircle2 className="w-5 h-5 shrink-0" />
              <span className="text-sm font-medium">Payouts connected — you're ready to sell paid listings.</span>
            </div>
            {earnings && (
              <div className="grid grid-cols-3 gap-3 text-center" data-testid="payout-earnings">
                <div className="p-3 rounded-lg bg-muted"><p className="text-xs text-muted-foreground">Sales</p><p className="font-semibold">{money(earnings.gross)}</p></div>
                <div className="p-3 rounded-lg bg-muted"><p className="text-xs text-muted-foreground">Platform fees</p><p className="font-semibold">{money(earnings.platform_fees)}</p></div>
                <div className="p-3 rounded-lg bg-muted"><p className="text-xs text-muted-foreground">Paid to you</p><p className="font-semibold">{money(earnings.net_to_seller)}</p></div>
              </div>
            )}
            <Button variant="outline" size="sm" onClick={openStripeDashboard}>
              <ExternalLink className="w-4 h-4 mr-2" />Open Stripe dashboard
            </Button>
          </div>
        ) : status?.connected ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-700">
              <AlertCircle className="w-5 h-5 shrink-0" />
              <span className="text-sm font-medium">Almost there — finish verifying your details with Stripe to start receiving payouts.</span>
            </div>
            {status.requirements_due?.length > 0 && (
              <p className="text-xs text-muted-foreground">Stripe still needs: {status.requirements_due.map(r => r.replace(/[._]/g, ' ')).join(', ')}</p>
            )}
            <Button onClick={startOnboarding} disabled={connecting} className="bg-[#003366] hover:bg-[#003366]/90">
              {connecting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Finish Setup
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <Badge className="bg-gray-100 text-gray-600">Not connected</Badge>
            <p className="text-sm text-muted-foreground">
              Setup takes a few minutes and is completed by you (the account owner) on Stripe's secure form: organisation
              details, an authorised representative and the bank account to be paid into. Until it is done you can't charge
              for paid courses, events or programmes.
            </p>
            <Button onClick={startOnboarding} disabled={connecting} className="bg-[#003366] hover:bg-[#003366]/90">
              {connecting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Connect Payouts
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
